import { useState, ReactNode, useEffect, useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronLeftIcon from "@atlaskit/icon/core/chevron-left";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { DropdownType } from "@/patterns/global-nav/src/mobile-nav/mobile-nav";

const mobileLogoCss = css({
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const tabletLogoCss = css({
    display: "none",
    [media.above.sm]: {
        display: "flex",
        alignItems: "center"
    }
});

const logoCss = css({
    height: "21px"
});

const backButtonCss = css({
    color: token("color.text.selected"),
    display: "flex",
    alignItems: "center"
});

const chevronCss = css({
    marginBlock: "6px 0"
});

const mobileIconContainerCss = css({
    height: "40px",
    display: "flex",
    alignItems: "center",
    [media.above.sm]: {
        display: "none"
    }
});

const mobileDropdownMenuCss = css({
    display: "none",

    [media.above.sm]: {
        opacity: 1,
        inset: "0px 0px auto auto",
        insetBlockStart: "100%",
        minWidth: "380px",
        width: "max-content",
        position: "absolute",
        height: "calc(100vh - 65px)",
        transform: "translate3d(-92%, 56px, 0px)",
        boxSizing: "border-box",
        overflow: "auto",
        borderRadius: "20px",
        // eslint-disable-next-line local/ensure-wac-token-usage
        border: "10px solid #fff",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0 0 1px 0 #ccc, 0 5px 20px -5px rgba(0,0,0,0.1)",
        order: 100,
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#ffffff",
        insetInlineStart: 0,
        transition: "width 200ms ease",
        zIndex: 10,
        animation: "fadein 0.25s 1"
    }
});

const tabletIconContainerCss = css({
    display: "none",

    [media.above.sm]: {
        display: "flex",
        alignItems: "center",
        position: "relative"
    }
});

const dropdownCss = css({
    display: "none",
    cursor: "pointer",
    [media.below.md]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "rgba(245, 40, 145, 0)",
        border: "none",
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#0052cc",
        display: "block",
        paddingBlockStart: token("space.075")
    }
});

type MobileSubNavTopProps = {
    productKey: ProductKeyTypes;
    showMenu: boolean;
    handleToggleMenu: (showMenu: boolean) => void;
    menuPage: DropdownType | string;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    handleFirstOpen: (firstOpen: boolean) => void;
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    tabletDropdownMenu: ReactNode;
};

export const MobileSubNavTop = ({
    productKey,
    showMenu,
    handleToggleMenu,
    menuPage,
    handleMenuPage,
    handleFirstOpen,
    mobileLogo,
    mobileLogoAriaLabel = "Atlassian logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "subNavWac",
    tabletDropdownMenu
}: MobileSubNavTopProps) => {
    const intl = useIntl();
    const [mobileDropdownMenu, setMobileDropdownMenu] =
        useState<HTMLElement | null>(null);

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            const path = event.composedPath && event.composedPath();
            if (mobileDropdownMenu) {
                const style = window.getComputedStyle(mobileDropdownMenu);
                const insideTabletMobileDropdownMenu =
                    Array.prototype.includes.call(
                        Array.prototype.map.call(path, (el: { id: string }) => {
                            return (
                                el.id ===
                                "nav-tablet-mobile-dropdown-menu-button"
                            );
                        }),
                        true
                    );

                if (
                    !insideTabletMobileDropdownMenu &&
                    style.display !== "none"
                ) {
                    handleToggleMenu(false);
                    handleMenuPage("default");
                    handleFirstOpen(false);
                }
            }
        },
        [mobileDropdownMenu, handleToggleMenu, handleMenuPage, handleFirstOpen]
    );

    const handleKeydown = useCallback(
        (event: KeyboardEvent) => {
            if (event.code == "Escape" && showMenu) {
                handleToggleMenu(false);
                handleMenuPage("default");
                handleFirstOpen(false);
            }
        },
        [handleToggleMenu, handleMenuPage, handleFirstOpen, showMenu]
    );

    useEffect(() => {
        const element = document.getElementById(
            "nav-tablet-mobile-dropdown-menu-button"
        );
        setMobileDropdownMenu(element);
    }, []);

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        document.addEventListener("keydown", handleKeydown);

        return () => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeydown);
        };
    }, [handleClickOutside, handleKeydown]);

    return (
        <>
            <div css={mobileLogoCss} className="mobile-nav-company-logo">
                {menuPage === "default" || !showMenu ? (
                    <a
                        css={logoCss}
                        href={mobileLogoHref}
                        aria-label={mobileLogoAriaLabel}
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container={mobileLogoDataEventContainer}
                        data-product-key={productKey}
                    >
                        {mobileLogo}
                    </a>
                ) : (
                    <button
                        style={{ all: "unset", width: "100%" }}
                        onClick={(e) => {
                            e.preventDefault();
                            handleMenuPage("default");
                            handleFirstOpen(false);
                        }}
                    >
                        <div css={backButtonCss}>
                            <div css={chevronCss}>
                                <ChevronLeftIcon label="" />
                            </div>
                            <span>
                                {intl.formatMessage({
                                    id: "subnav.menu-back",
                                    defaultMessage: "Back"
                                })}
                            </span>
                        </div>
                    </button>
                )}
            </div>
            <div css={tabletLogoCss} className="tablet-nav-company-logo">
                <a
                    css={logoCss}
                    href={mobileLogoHref}
                    aria-label={mobileLogoAriaLabel}
                    data-event="clicked"
                    data-event-component="linkButton"
                    data-event-container={mobileLogoDataEventContainer}
                    data-product-key={productKey}
                >
                    {mobileLogo}
                </a>
            </div>
            {/* Mobile button */}
            <div css={mobileIconContainerCss}>
                <button
                    css={dropdownCss}
                    style={{ all: "unset" }}
                    aria-label={intl.formatMessage({
                        id: "subnav.menu-icon.toggle",
                        defaultMessage: "Toggle Menu"
                    })}
                    className="subnavToggleMenu"
                    onClick={(e) => {
                        e.preventDefault();
                        handleToggleMenu(!showMenu);
                        handleMenuPage("default");
                        handleFirstOpen(!showMenu);
                    }}
                >
                    <svg
                        css={[
                            css({ transition: "transform .25s" }),
                            showMenu && css({ transform: "rotate(180deg)" })
                        ]}
                        width="18"
                        height="11"
                        viewBox="0 0 18 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M17 1.86328L9 9.13601L1 1.86328"
                            stroke="#101214"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
            {/* Tablet button with dropdown */}
            <div
                css={tabletIconContainerCss}
                id="nav-tablet-mobile-dropdown-menu-button"
            >
                <button
                    css={dropdownCss}
                    style={{ all: "unset" }}
                    aria-label={intl.formatMessage({
                        id: "subnav.menu-icon.toggle",
                        defaultMessage: "Toggle Menu"
                    })}
                    className="subnavToggleMenu"
                    onClick={(e) => {
                        e.preventDefault();
                        handleToggleMenu(!showMenu);
                        handleMenuPage("default");
                        handleFirstOpen(!showMenu);
                    }}
                >
                    <svg
                        css={[
                            css({ transition: "transform .25s" }),
                            showMenu && css({ transform: "rotate(180deg)" })
                        ]}
                        width="18"
                        height="11"
                        viewBox="0 0 18 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M17 1.86328L9 9.13601L1 1.86328"
                            stroke="#101214"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <div
                    className="mobile-dropdown-menu"
                    css={[
                        mobileDropdownMenuCss,
                        showMenu &&
                            css({
                                [media.above.sm]: {
                                    display: "block"
                                }
                            })
                    ]}
                >
                    <div
                        css={[
                            css({
                                marginBlockStart: "8px",
                                marginBlockEnd: "8px",
                                display: "none",
                                justifyContent: "flex-start",
                                alignItems: "center"
                            }),
                            menuPage !== "default" && css({ display: "flex" })
                        ]}
                    >
                        <button
                            style={{ all: "unset" }}
                            onClick={(e) => {
                                e.preventDefault();
                                handleMenuPage("default");
                                handleFirstOpen(false);
                            }}
                        >
                            <div css={backButtonCss}>
                                <div css={chevronCss}>
                                    <ChevronLeftIcon label="" />
                                </div>
                                <span>
                                    {intl.formatMessage({
                                        id: "subnav.menu-back",
                                        defaultMessage: "Back"
                                    })}
                                </span>
                            </div>
                        </button>
                    </div>
                    {tabletDropdownMenu}
                </div>
            </div>
            {showMenu && (
                <div
                    className="overlay"
                    css={css({
                        position: "fixed",
                        display: "block",
                        width: "100%",
                        height: "100%",
                        insetInlineStart: "0",
                        insetInlineEnd: "0",
                        insetBlockEnd: "0",
                        // eslint-disable-next-line local/ensure-wac-token-usage
                        backgroundColor: "rgba(255,255,255,0.7)",
                        zIndex: "-1"
                    })}
                />
            )}
        </>
    );
};
